import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { importLignesExcel } from '../../reduxStore/LigneDocumentVenteSlice';
import api from '../../reduxStore/api';
import { labels } from '../../constants/labels';

const PREVIEW_COLUMNS = ['article_id', 'article_code', 'quantite', 'prix_unitaire', 'remise', 'tva', 'depot_id', 'type_ligne', 'libelle_libre', 'extra_note'];

// Colonnes du modèle d'import articles (ArticleController.getTemplate) — le fichier
// « articles manquants » est directement réimportable depuis la page Articles.
const ARTICLE_TEMPLATE_HEADERS = ['code', 'designation', 'prix_v', 'prix_r', 'tva', 'remise', 'stock_securite', 'poids', 'unite', 'unite_sec', 'pieces_par_carton', 'track_stock', 'visible', 'allow_pcs_cts', 'famille_nom', 'fournisseur_nom'];

interface ImportResult {
  success: number;
  errors: Array<{ row: number; message: string }>;
  articles_manquants?: Array<{ code: string; lignes: number[] }>;
  ignorees?: number[];
}

/** « 4, 7, 9 » — tronqué au-delà de 20 numéros de ligne. */
const formatLignes = (lignes: number[]) =>
  lignes.length <= 20 ? lignes.join(', ') : `${lignes.slice(0, 20).join(', ')} … (+${lignes.length - 20})`;

/** Fichier à compléter puis importer via la page Articles. Le code est repris tel quel. */
const downloadArticlesManquants = (manquants: Array<{ code: string; lignes: number[] }>) => {
  const workbook = XLSX.utils.book_new();
  // Première feuille = format du modèle articles (lue par l'import articles).
  const articles = manquants.map((m) => ({ code: m.code }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(articles, { header: ARTICLE_TEMPLATE_HEADERS }), 'Articles');
  const detail = manquants.map((m) => ({ code: m.code, occurrences: m.lignes.length, lignes_fichier: m.lignes.join(', ') }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(detail), 'Detail');
  XLSX.writeFile(workbook, 'articles_manquants.xlsx');
};

interface Props {
  open: boolean;
  onClose: () => void;
  documentVenteId: number;
  depotId: number;
  onImported: () => void;
}

const ImportLignesDialog: React.FC<Props> = ({ open, onClose, documentVenteId, depotId, onImported }) => {
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleClose = () => {
    setRows([]);
    setFileName(null);
    setResult(null);
    setParseError(null);
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/ligneDocumentVentes/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_lignes.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null);
    setParseError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsed = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: null });
        const normalized = parsed
          .filter(row => Object.values(row).some(v => v !== null && v !== ''))
          .map(row => {
            const out: Record<string, any> = {};
            for (const [k, v] of Object.entries(row))
              out[k] = v instanceof Date ? v.toISOString().slice(0, 10) : v;
            return out;
          });
        if (normalized.length === 0) {
          setParseError('Le fichier ne contient aucune ligne valide.');
          setRows([]);
        } else if (normalized.length > 500) {
          setParseError(`Le fichier contient ${normalized.length} lignes — maximum autorisé : 500.`);
          setRows([]);
        } else {
          setRows(normalized);
        }
      } catch {
        setParseError('Impossible de lire le fichier. Vérifiez qu\'il s\'agit d\'un fichier Excel (.xlsx).');
        setRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    try {
      const action = await dispatch(importLignesExcel({
        document_vente_id: documentVenteId,
        depot_id: depotId,
        lignes: rows,
      }));
      if (importLignesExcel.fulfilled.match(action)) {
        setResult(action.payload);
        if (action.payload.errors.length === 0) {
          onImported();
        }
      } else {
        setResult({ success: 0, errors: [{ row: 0, message: String(action.payload ?? 'Erreur inconnue') }] });
      }
    } finally {
      setImporting(false);
    }
  };

  const previewCols = rows.length > 0
    ? PREVIEW_COLUMNS.filter(col => rows.some(r => r[col] != null && r[col] !== ''))
    : [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Importer des lignes depuis Excel</DialogTitle>

      <DialogContent dividers>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Télécharger le modèle
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
          >
            {fileName ? 'Changer le fichier' : 'Choisir un fichier'}
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {fileName} — {rows.length} ligne{rows.length > 1 ? 's' : ''}
            </Typography>
          )}
          <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
        </Box>

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

        {result && result.errors.length === 0 && result.success > 0 && (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
            {result.success} ligne{result.success > 1 ? 's' : ''} importée{result.success > 1 ? 's' : ''} avec succès.
          </Alert>
        )}

        {result && result.errors.length === 0 && result.success === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Aucune ligne importée : toutes les lignes ont une quantité vide ou à 0.
          </Alert>
        )}

        {result && result.errors.length === 0 && result.success > 0 && !!result.ignorees?.length && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {result.ignorees.length} ligne{result.ignorees.length > 1 ? 's' : ''} ignorée{result.ignorees.length > 1 ? 's' : ''} (quantité vide ou 0) : ligne{result.ignorees.length > 1 ? 's' : ''} {formatLignes(result.ignorees)}
          </Alert>
        )}

        {result && !!result.articles_manquants?.length && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => downloadArticlesManquants(result.articles_manquants!)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Télécharger
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              {result.articles_manquants.length} article{result.articles_manquants.length > 1 ? 's' : ''} inexistant{result.articles_manquants.length > 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
              {result.articles_manquants.map((m) => m.code).join(' · ')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Téléchargez le fichier au format du modèle articles, complétez-le, importez-le depuis « {labels.gestionArticles} », puis relancez cet import.
            </Typography>
          </Alert>
        )}

        {result && result.errors.length > 0 && (
          <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Aucune ligne importée — {result.errors.length} erreur{result.errors.length > 1 ? 's' : ''} détectée{result.errors.length > 1 ? 's' : ''} :
            </Typography>
            {result.errors.map((e) => (
              <Typography key={e.row} variant="body2">
                • Ligne {e.row} : {e.message}
              </Typography>
            ))}
          </Alert>
        )}

        {rows.length > 0 && result === null && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
              Aperçu — {rows.length} ligne{rows.length > 1 ? 's' : ''} à importer
            </Typography>
            <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', bgcolor: 'grey.100' }}>#</TableCell>
                    {previewCols.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', bgcolor: 'grey.100', whiteSpace: 'nowrap' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>{idx + 2}</TableCell>
                      {previewCols.map(col => (
                        <TableCell key={col} sx={{ fontSize: '0.8rem', fontFamily: typeof row[col] === 'number' ? 'monospace' : undefined }}>
                          {row[col] != null && row[col] !== '' ? String(row[col]) : <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Fermer</Button>
        {result === null && (
          <Button
            variant="contained"
            disabled={rows.length === 0 || importing}
            onClick={handleImport}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {importing ? 'Import en cours…' : `Importer ${rows.length > 0 ? rows.length + ' ligne' + (rows.length > 1 ? 's' : '') : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportLignesDialog;
